import { Inject, Injectable, Optional } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { first, Subscription } from 'rxjs';
import { abTestConfigInterface } from '@ab-test';
declare let dataLayer: Array<string | object>;
declare let hams: {
  track_post_url: () => void;
  prepareCustomEvent(event: unknown): () => void;
  send_hamstr_q(send_option: 'post_url'): () => void;
};
declare let gtag: (...parameters) => void;

@Injectable({
  providedIn: 'root'
})
export class TagManagerService {
  constructor(
    private platformService: PlatformInformationService,
    @Optional() @Inject(DOCUMENT) private document: Document
  ) {
    if (platformService.getIsBrowser() && platformService.isProductionEnvironment()) {
      setTimeout(() => {
        this.injectHAMStracker();
      }, 8000);
    }
  }

  unpushedParams = [];
  unpushedGA4 = [];
  alreadyPushed = [];
  injected = false;
  dataSub: Subscription;
  hamsBuffer = [];

  isTagManagerActive() {
    return typeof dataLayer !== 'undefined' && dataLayer;
  }

  /**
   * push given data to the tagmanager. This will work even when the
   * tagManager has not been booted yet. All pushes will sent once the
   * tagManager is active.
   * You can use the unique param to only push a given information once
   * and not anymore after that.
   * @param parameters the param object you want to push
   * @param unique a unique identifier to prevent double pushing
   */
  push(parameters: object, unique?: string, reportHams = true) {
    // not a unique push or unique and not already pushed
    if (!unique || (unique && !this.alreadyPushed.includes(unique))) {
      if (unique) {
        this.alreadyPushed.push(unique);
      }

      // Track for HAMS
      if (reportHams && typeof hams !== 'undefined' && parameters['event'] !== 'impression') {
        hams.prepareCustomEvent(parameters);
        hams.send_hamstr_q('post_url');
      } else {
        this.hamsBuffer.push(parameters);
      }

      // Track in Tagmanager
      if (this.isTagManagerActive() && dataLayer !== undefined) {
        try {
          if (Object.prototype.hasOwnProperty.call(parameters, 'gtagType')) {
            gtag(parameters['gtagType'], parameters['gtagName'], parameters['parameters']);
          } else {
            dataLayer.push(parameters);
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        this.unpushedParams.push(parameters);
      }
    }
  }

  /* istanbul ignore next */
  injectGoogleTagManager() {
    if (!this.injected) {
      this.injected = true;
      const host = 'https://www.googletagmanager.com';
      this.dataSub = this.platformService.data$.pipe(first()).subscribe(data => {
        // probably don't need that data anymore
        if (data.tagManagerContainerId) {
          if (!this.platformService.isLiveProd()) {
            data.tagManagerContainerId = this.platformService.getTagManagerId();
          }
          const node = this.document.createElement('script');
          node.type = 'text/javascript';
          // tslint:disable-next-line:quotemark max-line-length
          let code =
            "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='%host%/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','%tagid%');document.gtm_data = {};";
          code = code.replace('%tagid%', data.tagManagerContainerId).replace('%host%', host);
          node.innerHTML = code;

          node.async = true;
          node.charset = 'utf-8';
          const head = this.document.getElementsByTagName('head')[0];
          head.insertBefore(node, head.firstChild);

          const bodyNode = this.document.createElement('noscript');
          const iframe = this.document.createElement('iframe');
          iframe.src = '//www.googletagmanager.com/ns.html?id=' + data.tagManagerContainerId;
          iframe.height = '0';
          iframe.width = '0';
          iframe.style.display = 'none';
          iframe.style.visibility = 'hidden';
          bodyNode.appendChild(iframe);
          const body = this.document.getElementsByTagName('body')[0];
          body.insertBefore(bodyNode, body.firstChild);
          iframe.onload = () => {
            this.dataSub?.unsubscribe();
            this.unpushedParams.forEach(params => {
              this.push(params);
            });
          };
        }
      });
    }
  }

  /**
   * injects as a low priority script from HAMS that will parse after DOM completed
   * It will track all TagManager events as well as AB test data that have been buffered before execution
   */
  private injectHAMStracker() {
    const identifier = 'hamstracker';
    if (!this.document.getElementById(identifier)) {
      const node = this.document.createElement('script');
      node.type = 'text/javascript';
      node.src = 'https://ts.cdi2.alpy.com/cdi2.js';
      node.id = identifier;
      node.defer = true;
      node.onload = () => {
        hams.track_post_url();
        this.hamsBuffer.forEach(param => {
          hams.prepareCustomEvent(param);
          hams.send_hamstr_q('post_url');
        });
      };
      const head = this.document.getElementsByTagName('head')[0];

      head?.appendChild(node);
    }
  }

  /**
   * 2024 HAMS ab test reporting. Is supposed to replace analytics ab test tracking completely
   * @param test the test to report
   * @param version the version a customer is seeing
   */
  activateExperiment(test: abTestConfigInterface, version: number) {
    const hamsData = {
      hitType: 'event',
      eventCategory: 'ABX',
      eventAction: version,
      eventLabel: test.scope
    };
    if (typeof hams !== 'undefined') {
      hams.prepareCustomEvent(hamsData);
      hams.send_hamstr_q('post_url');
    } else {
      this.hamsBuffer.push(hamsData);
    }
  }
}
